import React, {
  createContext,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { RouteComponentProps } from 'react-router';
import { Box } from '@chakra-ui/react';

import { BodyProps, IAccordion } from 'constants/MultipleAcorddions';

import { MultipleAccordion, Loading } from 'components';
import { readALI, save, sendEmail, result } from 'services/ALI';
import { openNotification } from 'utils/notification';
import {
  GoalConcret,
  Legality,
  Necessity,
  RelevantConceptsAndActivity,
  SensitiveData,
  Transparency,
  Conclusions,
} from './components/Forms';
import ALIData, { IALIResult } from 'models/Forms/ALI';

import { getLoggedUser } from 'utils/storage';
import useEventBus from 'hooks/useEventBus';
import { getALIFinishPosition } from 'services/ALIConclusion';

type stClient = (d: ALIData) => ALIData;
export interface ALIProps extends BodyProps {}
interface IALICtx {
  data: ALIData;
  isDetails: boolean;
  handlePayload: (data: ALIData | stClient) => void;
  client?: { name: string; id: React.ReactText };
  setClient?: React.Dispatch<React.SetStateAction<IALICtx['client']>>;
  results?: IALIResult;
  unfilledSteps: string[];
  load: boolean;
}

export const ALICtx = createContext<IALICtx>({
  data: new ALIData(),
  isDetails: false,
  results: undefined as IALIResult | undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handlePayload: (data: ALIData | stClient) => {},
  unfilledSteps: [] as string[],
  load: false,
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ALIPage: React.FC<RouteComponentProps<{ id?: string }>> = ({ match, history }) => {
  const eventBus = useEventBus();
  useEffect(() => {
    eventBus.dispatchEvent('onChangeTitle', 'Avaliação de Legítimo Interesse');
  }, []);
  const id = useMemo(() => match.params.id, [match.params]);
  const [results, setResults] = useState<IALIResult>();
  const [client, setClient] = useState<IALICtx['client']>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [enableConclusion, setEnableConclusion] = useState(false);
  const payloadRef = useRef(new ALIData());
  const [hasUpdate, setHasUpdate] = useState(Date.now());
  const [isLoading, setIsLoading] = useState(false);
  const [completed, setCompleted] = useState<any>(false);
  const [load, setIsLoad] = useState(false);

  const unfilledStepsRef = useRef([
    '1 - Conceitos relevantes e descrição da atividade',
    '3 - Finalidade concreta',
    '4 - Licitude',
    '5 - Necessidade',
    '6 - Transparência',
  ]);
  const { payload, unfilledSteps } = useMemo(
    () => ({
      payload: payloadRef.current,
      unfilledSteps: unfilledStepsRef.current,
    }),
    [payloadRef.current, unfilledStepsRef.current, hasUpdate],
  );

  const handlePayload = useCallback((data: ALIData | stClient) => {
    if (typeof data === 'function') {
      const dt = data(payloadRef.current);
      payloadRef.current = dt;
    } else {
      payloadRef.current = { ...payloadRef.current, ...data };
    }
    setHasUpdate(Date.now());
  }, []);

  async function submit(dt: ALIData, step: string, complete?: boolean) {
    setIsLoad(true);
    if (complete && unfilledStepsRef.current.length > 1) {
      try {
        if (id) {
          const { data } = await readALI(id);
          setEnableConclusion(data.completed || false);
          setTimeout(() => setEnableConclusion(false), 3000);
          return true;
        }
      } catch (err) {}
    }

    try {
      openNotification('Salvando...', 'info');

      const pay = {
        ...payloadRef.current,
        ...dt,
        jsonCustomObs: JSON.stringify(payloadRef.current.jsonCustomObsObj),
      };
      // unfilledStepsRef.current = unfilledStepsRef.current.filter(i => i !== step);
      const { data } = await save(pay);

      const finishForm = Number(step.slice(0, 1));

      if (data.id !== undefined) {
        const response = await getALIFinishPosition(finishForm, data.id);
        if (response.data) {
          unfilledStepsRef.current = unfilledStepsRef.current.filter(i => i !== step);
        }
      }

      handlePayload({ ...pay, id: data.id });
      if (complete) {
        if (data.id) {
          openNotification('Concluindo...', 'info');
          const d = await result(data.id);

          if (d.data) {
            setResults(d.data);
            try {
              await sendEmail(data.id);
              openNotification('Concluído com sucesso', 'success');
              setEnableConclusion(true);
            } catch (err) {}
          } else {
            openNotification('Erro ao concluir avaliação', 'error');
          }
        } else {
          openNotification('Erro ao concluir avaliação', 'error');
        }
      } else {
        openNotification('Salvo com sucesso', 'success');
      }
      setIsLoad(false);
      return true;
    } catch (error) {
      setIsLoad(false);
    }
  }

  const accordionsItems: IAccordion[] = [
    {
      title: 'Conceitos relevantes e descrição da atividade',
      // eslint-disable-next-line react/display-name
      body: props => <RelevantConceptsAndActivity {...props} />,
      submit: true,
    },
    {
      title: 'Dados sensíveis',
      // eslint-disable-next-line react/display-name
      body: props => <SensitiveData {...props} />,
      submit: true,
      // disabled: true,
    },
    {
      title: 'Finalidade concreta',
      // eslint-disable-next-line react/display-name
      body: props => <GoalConcret {...props} />,
      submit: true,
      // disabled: true,
    },
    {
      title: 'Licitude',
      // eslint-disable-next-line react/display-name
      body: props => <Legality {...props} />,
      submit: true,
      // disabled: true,
    },
    {
      title: 'Necessidade',
      // eslint-disable-next-line react/display-name
      body: props => <Necessity {...props} />,
      submit: true,
      // disabled: true,
    },
    {
      title: 'Transparência',
      // eslint-disable-next-line react/display-name
      body: props => <Transparency {...props} />,
      submit: true,
      // disabled: true,
    },
    {
      title: 'Resultado final',
      // eslint-disable-next-line react/display-name
      body: props => <Conclusions unfilledSteps={unfilledSteps} isDone={completed} {...props} />,
      submit: false,
      // disabled: !enableConclusion,
    },
  ];

  useEffect(() => {
    if (getLoggedUser().clientName) {
      setClient({
        name: getLoggedUser().clientName || '',
        id: getLoggedUser().client || 0,
      });
      handlePayload({ ...payloadRef.current, clientId: getLoggedUser().client });
    }
    if (id) {
      (async () => {
        setIsLoading(true);
        setIsLoad(true);
        try {
          const { data } = await readALI(id);

          setCompleted(data.completed);
          const { data: results } = await result(id);
          setResults(results);
          unfilledStepsRef.current = [];
          handlePayload({
            ...data,
            jsonCustomObsObj: JSON.parse(data.jsonCustomObs),
            activityDataSources: data.activityDataSources,
          });
          setEnableConclusion(data.completed || false);
          setIsLoading(false);
          setIsLoad(false);
        } catch (err) {
          openNotification('Ocorreu um erro ao carregar os dados', 'error');
        } finally {
          setTimeout(() => setIsLoading(false), 400);
        }
      })();
    }
  }, [id]);

  return (
    <Box w="100%">
      <Box m="0 auto" maxW="50rem" width="100%">
        {/* <Stack direction={['column', 'row']} spacing="1rem" mb="1rem">
          <Link mt="0.5em" onClick={history.goBack}>
            <Icon size={1.6} name={'arrowLeft'} iconColor={600} />
          </Link>
          <Heading mb="1rem" size="lg" as="h3">
            Avaliação de Legítimo Interesse
          </Heading>
        </Stack> */}
        {isLoading ? (
          <Loading />
        ) : (
          <ALICtx.Provider
            value={{
              data: payload,
              isDetails: !!id,
              handlePayload,
              client,
              setClient,
              results,
              unfilledSteps,
              load,
            }}
          >
            <MultipleAccordion
              allowMultiple={completed && Object.keys(payload).length > 0}
              accordions={accordionsItems}
              completed={completed}
              runOnChange={submit}
              // completed={completed}
            />
          </ALICtx.Provider>
        )}
      </Box>
    </Box>
  );
};

export const ALI = memo(ALIPage);
